// starter-lineage.ts — template lineage for the STARTERS catalog path.
//
// Background (two instantiation paths, one lineage shape):
//
//  - The registry path (`instantiate-service.ts` →
//    `instantiateTemplateToProject`) compiles a registered
//    `cogo.template.v1` package and persists
//    `settings.cogoApplicationMetadata.instantiatedFrom` via
//    `persistProjectTemplateLineage`. Its `structureHash` is the value the
//    package was AUTHORED against (`pkg.source.structureHash`) — an
//    independently registered constant, so a served-source drift is
//    detectable by comparing against it.
//
//  - The Starters/showroom path (`/studio/templates` gallery →
//    `createServerProject(name, templateId)`) seeds a project from a plain
//    catalog template under `/apps/{templateId}/`. Those templates are NOT
//    `cogo.template.v1` packages: there is no registered package, no slot
//    declarations, and therefore no authored structureHash anywhere.
//
// This module supplies the identity the Starters path CAN prove, using the
// same lineage shape and the same canonical hash algorithm, so both paths
// write one shape to one key:
//
//   templateId    — the catalog id (`fitness-gym-new3`), real and stable.
//   version       — `app.json`'s own `version` field, as served.
//   structureHash — sha256 over the canonical JSON of the exact bundle that
//                   was fetched and seeded, computed with @cogo/template's
//                   `computeStructureHash` (empty slot set, design file NOT
//                   masked — catalog templates declare no slots and no
//                   design binding).
//
// HONEST BOUNDARY — read this before treating the two hashes as equivalent:
// a Starters `structureHash` is an OBSERVED hash of what the server served at
// instantiation time, not a REGISTERED one. It proves "these are the bytes
// this project was seeded from" and it changes when the served template
// changes, so it discriminates template versions and detects post-hoc source
// edits across projects. It CANNOT, on its own, prove agreement with an
// independently registered package, because no such package exists for these
// templates. Registering the catalog templates as `cogo.template.v1` packages
// (`TEMPLATE_REGISTRY`) is the upstream change that would upgrade this to a
// registered hash — see this module's tests and the lane report.

import { computeStructureHash, type AppBundle, type Json } from '@cogo/template'
import type { TemplateLineage } from './content-form/project-lineage'

/**
 * Catalog templates declare no slots and no design rebinding, so the mask is
 * the identity function and `computeStructureHash` degenerates to "sha256 of
 * the canonical JSON of every served file". `rebinding: 'pinned'` is the
 * choice that never throws: `maskBundle` throws when `rebinding` is
 * `'allowed'` and the bundle has no `design-system.json`, which is true of
 * some catalog templates.
 */
const NO_DESIGN_REBINDING = {
  file: 'design-system.json',
  rebinding: 'pinned',
} as const

function readVersion(manifest: unknown): string | null {
  if (manifest === null || typeof manifest !== 'object' || Array.isArray(manifest)) return null
  const version = (manifest as Record<string, unknown>).version
  if (typeof version !== 'string') return null
  const trimmed = version.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Derives the `instantiatedFrom` lineage for a project seeded from a Starters
 * catalog template.
 *
 * Returns `null` — never a placeholder — when the served template cannot
 * supply a real identity (no `app.json`, no `version` string on it, or a
 * bundle that is not canonical JSON). A missing lineage is the honest
 * outcome there: `readInstantiatedFrom` requires all three fields to be
 * strings, and writing a fabricated `version`/`structureHash` would make an
 * unidentifiable project look identified.
 */
export async function deriveStarterTemplateLineage(
  templateId: string,
  files: Record<string, unknown>,
): Promise<TemplateLineage | null> {
  if (!templateId) return null

  const version = readVersion(files['app.json'])
  if (!version) return null

  let structureHash: string
  try {
    structureHash = await computeStructureHash(
      { files: files as Record<string, Json> } as AppBundle,
      [],
      NO_DESIGN_REBINDING,
    )
  } catch (err) {
    console.warn('[starter-lineage] could not hash template bundle:', err)
    return null
  }

  return { templateId, version, structureHash }
}
